/**
 * Shared serialisation for the two generated texture layers.
 *
 * Extracted once the second caller landed, not before — grain (#21) shipped
 * with this inline, and the review's guidance was to wait for a real second
 * use rather than abstract on the first.
 *
 * It exists because the encoding rules below are subtle, silent when broken,
 * and identical for both layers. Grain lost a whole render to one of them
 * before it shipped; duplicating the reasoning into a second file is how the
 * second layer would lose it too.
 */

export const SVG = 'http://www.w3.org/2000/svg'

/**
 * Serialise an SVG element to a `data:image/svg+xml` URI safe to drop into a
 * CSS `url()`, quoted or bare.
 *
 * Two rules, both learned the hard way on #21:
 *
 * 1. **Never `setAttribute('xmlns', SVG)` on the element you pass in.**
 *    `createElementNS` has already put it in that namespace and the serializer
 *    emits the declaration itself; setting the literal attribute as well
 *    creates a second, null-namespace attribute spelled `xmlns` and the output
 *    carries it twice. A repeated attribute name is a fatal XML
 *    well-formedness error and `image/svg+xml` is parsed as XML, so the result
 *    is a parse error rather than a picture — total absence, not degradation.
 *
 * 2. **`encodeURIComponent` is not sufficient on its own.** It leaves `(`,
 *    `)` and `'` literal. `#` it does encode, which matters because CSS reads
 *    `url("data:…#g")` as a fragment and truncates the value mid-document; but
 *    an unencoded `)` closes a bare `url(data:…)` early, and both layers put
 *    parens in the payload — grain via `url(#g)`, the mesh via every
 *    `rgba(…)` ink value. Encoding them costs a few bytes and makes the URI
 *    correct quoted or unquoted, rather than leaving every caller owing a pair
 *    of quotes that nothing wrote down.
 */
export function toSvgDataUri(svg: Element): string {
  const markup = new XMLSerializer().serializeToString(svg)
  const encoded = encodeURIComponent(markup).replace(
    /[()']/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  )
  return `data:image/svg+xml;charset=utf-8,${encoded}`
}
